import React, { useState, useEffect, useRef, useCallback } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTranslation } from "react-i18next"
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Badge,
  Slide,
  Avatar,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Divider,
  Stack,
  Chip,
  CircularProgress,
  InputAdornment,
} from "@mui/material"
import ChatIcon from "@mui/icons-material/Chat"
import CloseIcon from "@mui/icons-material/Close"
import ArrowBackIcon from "@mui/icons-material/ArrowBack"
import SendIcon from "@mui/icons-material/Send"
import CircleIcon from "@mui/icons-material/Circle"
import SearchIcon from "@mui/icons-material/Search"

import { io } from "socket.io-client"
import {
  toggleChatPanel,
  setActiveConversation,
  clearActiveConversation,
  fetchMessages,
  sendMessage,
  receiveMessage,
  setOnlineUsers,
  connectChatSuccess,
  connectChatFail,
  disconnectChat,
} from "../../store/realtimeChat/actions"
import {
  selectOnlineUsers,
  selectMessages,
  selectActiveConversation,
  selectTotalUnreadMessages,
  selectChatPanelOpen,
  selectConnected,
} from "../../store/realtimeChat/reducer"
import { API_URL } from "../../helpers/api_urls"

const ChatPanel = () => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const messagesEndRef = useRef(null)
  const socketRef = useRef(null)

  const chatPanelOpen = useSelector(selectChatPanelOpen)
  const connected = useSelector(selectConnected)
  const onlineUsers = useSelector(selectOnlineUsers)
  const activeConversation = useSelector(selectActiveConversation)
  const messages = useSelector((state) => selectMessages(state, activeConversation))
  const totalUnread = useSelector(selectTotalUnreadMessages)

  const [messageInput, setMessageInput] = useState("")
  const [searchTerm, setSearchTerm] = useState("")

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")

  // Initialize socket connection
  useEffect(() => {
    if (!socketRef.current && authUser?.session_token) {
      const socketUrl = API_URL.replace("/api/v2/", "")
      socketRef.current = io(socketUrl, {
        auth: { token: authUser.session_token },
        transports: ["websocket", "polling"],
      })

      socketRef.current.on("connect", () => {
        dispatch(connectChatSuccess())
      })

      socketRef.current.on("disconnect", () => {
        dispatch(disconnectChat())
      })

      socketRef.current.on("connect_error", (error) => {
        dispatch(connectChatFail(error.message))
      })

      socketRef.current.on("users:online", (users) => {
        dispatch(setOnlineUsers(users))
      })

      socketRef.current.on("message:receive", (message) => {
        dispatch(receiveMessage(message))
      })
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect()
        socketRef.current = null
      }
    }
  }, [authUser?.session_token, dispatch])

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" })
  }, [messages])

  // Load messages when conversation changes
  useEffect(() => {
    if (activeConversation) {
      dispatch(fetchMessages(activeConversation))
    }
  }, [activeConversation, dispatch])

  const handleTogglePanel = () => {
    dispatch(toggleChatPanel())
  }

  const handleSelectUser = (userId) => {
    dispatch(setActiveConversation(userId))
  }

  const handleBackToList = () => {
    dispatch(clearActiveConversation())
  }

  const handleSendMessage = () => {
    if (!messageInput.trim() || !activeConversation) return

    const message = {
      recipientId: activeConversation,
      content: messageInput.trim(),
      timestamp: new Date().toISOString(),
    }

    // Send via socket
    if (socketRef.current?.connected) {
      socketRef.current.emit("message:send", message)
    }

    dispatch(sendMessage(message))
    setMessageInput("")
  }

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault()
      handleSendMessage()
    }
  }

  const filteredUsers = onlineUsers.filter((user) =>
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const selectedUser = onlineUsers.find((u) => u.id === activeConversation)

  const formatTime = (timestamp) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  }

  return (
    <>
      {/* Chat Panel — drops down from header top-right */}
      <Slide direction="down" in={chatPanelOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={8}
          sx={{
            position: "fixed",
            top: 64,
            right: 16,
            width: 360,
            height: 500,
            zIndex: 1300,
            borderRadius: 3,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: "#3b82f6",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              {activeConversation ? (
                <>
                  <IconButton size="small" onClick={handleBackToList} sx={{ color: "#fff" }}>
                    <ArrowBackIcon fontSize="small" />
                  </IconButton>
                  <Avatar sx={{ width: 32, height: 32, bgcolor: "#fff", color: "#3b82f6" }}>
                    {selectedUser?.name?.charAt(0)?.toUpperCase() || "U"}
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>
                      {selectedUser?.name || t("User")}
                    </Typography>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      <CircleIcon sx={{ fontSize: 8, color: "#4ade80" }} />
                      <Typography variant="caption">{t("Online")}</Typography>
                    </Stack>
                  </Box>
                </>
              ) : (
                <>
                  <ChatIcon />
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t("Team Chat")}
                  </Typography>
                </>
              )}
            </Stack>
            <IconButton size="small" onClick={handleTogglePanel} sx={{ color: "#fff" }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Content */}
          {!activeConversation ? (
            // User List View
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {/* Search */}
              <Box sx={{ p: 1.5, borderBottom: "1px solid #e2e8f0" }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t("Search users...")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: "#9ca3af", fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: 2,
                      bgcolor: "#f8fafc",
                    },
                  }}
                />
              </Box>

              {/* Connection Status */}
              {!connected && (
                <Box sx={{ p: 2, textAlign: "center" }}>
                  <CircularProgress size={24} />
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                    {t("Connecting...")}
                  </Typography>
                </Box>
              )}

              {/* User List */}
              <List sx={{ flex: 1, overflow: "auto", py: 0 }}>
                {filteredUsers.length === 0 ? (
                  <Box sx={{ p: 3, textAlign: "center" }}>
                    <Typography variant="body2" color="text.secondary">
                      {connected ? t("No users online") : t("Connecting to server...")}
                    </Typography>
                  </Box>
                ) : (
                  filteredUsers.map((user) => (
                    <ListItemButton
                      key={user.id}
                      onClick={() => handleSelectUser(user.id)}
                      sx={{
                        py: 1.5,
                        "&:hover": { bgcolor: "#f8fafc" },
                      }}
                    >
                      <ListItemAvatar>
                        <Badge
                          overlap="circular"
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          badgeContent={
                            <CircleIcon
                              sx={{
                                fontSize: 12,
                                color: user.online ? "#4ade80" : "#9ca3af",
                                bgcolor: "#fff",
                                borderRadius: "50%",
                              }}
                            />
                          }
                        >
                          <Avatar sx={{ bgcolor: "#dbeafe", color: "#3b82f6" }}>
                            {user.name?.charAt(0)?.toUpperCase() || "U"}
                          </Avatar>
                        </Badge>
                      </ListItemAvatar>
                      <ListItemText
                        primary={user.name}
                        secondary={user.role || user.email}
                        primaryTypographyProps={{ fontWeight: 500, fontSize: "0.875rem" }}
                        secondaryTypographyProps={{ fontSize: "0.75rem" }}
                      />
                    </ListItemButton>
                  ))
                )}
              </List>
            </Box>
          ) : (
            // Conversation View
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
              {/* Messages */}
              <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                {messages.length === 0 ? (
                  <Box sx={{ textAlign: "center", py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("No messages yet. Start the conversation!")}
                    </Typography>
                  </Box>
                ) : (
                  <Stack spacing={1}>
                    {messages.map((msg, index) => {
                      const isOwn = msg.senderId === authUser.id
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            justifyContent: isOwn ? "flex-end" : "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: "75%",
                              px: 1.5,
                              py: 1,
                              borderRadius: 2,
                              bgcolor: isOwn ? "#3b82f6" : "#f1f5f9",
                              color: isOwn ? "#fff" : "#1e293b",
                            }}
                          >
                            <Typography variant="body2">{msg.content}</Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                display: "block",
                                textAlign: "right",
                                mt: 0.5,
                                opacity: 0.7,
                              }}
                            >
                              {formatTime(msg.timestamp)}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    })}
                    <div ref={messagesEndRef} />
                  </Stack>
                )}
              </Box>

              {/* Message Input */}
              <Box sx={{ p: 1.5, borderTop: "1px solid #e2e8f0" }}>
                <Stack direction="row" spacing={1}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder={t("Type a message...")}
                    value={messageInput}
                    onChange={(e) => setMessageInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                  <IconButton
                    color="primary"
                    onClick={handleSendMessage}
                    disabled={!messageInput.trim()}
                    sx={{
                      bgcolor: "#3b82f6",
                      color: "#fff",
                      "&:hover": { bgcolor: "#2563eb" },
                      "&:disabled": { bgcolor: "#e2e8f0", color: "#9ca3af" },
                    }}
                  >
                    <SendIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Box>
            </Box>
          )}
        </Paper>
      </Slide>
    </>
  )
}

export default ChatPanel
